import React, { useState, useEffect } from 'react'
import { db } from '../../helpers/firebase'
import { doc, updateDoc } from 'firebase/firestore'
import CelebrationOverlay from '../UiElements/CelebrationOverlay'
import './ParentTutorial.css'

const FAKE_STUDENT = {
  name: 'Emma Johnson',
  email: 'emma@example.com',
  initials: 'EJ'
}

const FAKE_QUESTIONS = [
  {
    id: 'q1',
    text: 'Solve for x: 2x + 5 = 13',
    topic: 'Algebra',
    date: 'April 12, 2026',
    correct: true,
    attempts: 1,
    feedback: 'Great work! You correctly isolated x by subtracting 5 from both sides, then dividing by 2 to get x = 4.'
  },
  {
    id: 'q2',
    text: 'Find the area of a circle with radius 5 cm',
    topic: 'Geometry',
    date: 'April 13, 2026',
    correct: true,
    attempts: 2,
    feedback: 'Correct! You remembered that A = πr² and calculated A ≈ 78.54 cm². Nice perseverance on the second attempt!'
  },
  {
    id: 'q3',
    text: 'What is 15% of 80?',
    topic: 'Number Sense',
    date: 'April 14, 2026',
    correct: false,
    attempts: 3,
    feedback: 'Not quite — 15% of 80 is 12 (0.15 × 80). Keep practising percentages!'
  }
]

const FAKE_PAGES = [
  { id: 'p1', number: 1, description: 'Introduction to Linear Equations', questions: 5 },
  { id: 'p2', number: 2, description: 'Quadratic Equations Practice', questions: 8 }
]

const STEPS = [
  {
    id: 'welcome',
    emoji: '👋',
    title: 'Welcome to Quillnote!',
    subtitle: "You're all set up as a parent.",
    description: "We'll give you a quick tour of your dashboard so you know exactly where everything lives. It only takes about a minute!",
    preview: 'welcome'
  },
  {
    id: 'students',
    emoji: '👩‍🎓',
    title: 'My Students',
    subtitle: 'Your children, at a glance.',
    description: 'This is where all of your linked student accounts appear. Click any student card to dive into their work and progress. You can add as many students as you need.',
    preview: 'students'
  },
  {
    id: 'view-work',
    emoji: '📊',
    title: 'View Work',
    subtitle: "See every question your student has attempted.",
    description: "When you open a student's profile, you'll see their full question history here — sorted by date, topic, or correctness. Click any question to see their attempts, AI feedback, and recommended videos.",
    preview: 'view-work'
  },
  {
    id: 'manage-work',
    emoji: '📄',
    title: 'Manage Work',
    subtitle: 'Upload pages for your student to practice.',
    description: 'Take a photo of any textbook page or worksheet. Quillnote will automatically extract the questions and add them to your student\'s practice queue. No manual typing needed!',
    preview: 'manage-work'
  },
  {
    id: 'settings',
    emoji: '⚙️',
    title: 'Settings & Notifications',
    subtitle: 'Stay in control and stay informed.',
    description: "From Settings, you can control the AI Chat Helper for each student and manage your email notifications — including daily summaries and weekly digests of your student's progress.",
    preview: 'settings'
  }
]

const WelcomePreview = () => (
  <div className="pt-preview-welcome">
    <div className="pt-preview-welcome-logo">
      <img src="/logo.svg" alt="Quillnote" className="pt-preview-logo-img" />
      <span className="pt-preview-logo-text brand-text">Quillnote</span>
    </div>
    <div className="pt-preview-welcome-cards">
      <div className="pt-preview-stat-card">
        <div className="pt-preview-stat-emoji">👩‍🎓</div>
        <div className="pt-preview-stat-label">My Students</div>
      </div>
      <div className="pt-preview-stat-card">
        <div className="pt-preview-stat-emoji">📊</div>
        <div className="pt-preview-stat-label">View Work</div>
      </div>
      <div className="pt-preview-stat-card">
        <div className="pt-preview-stat-emoji">📄</div>
        <div className="pt-preview-stat-label">Manage Work</div>
      </div>
      <div className="pt-preview-stat-card">
        <div className="pt-preview-stat-emoji">⚙️</div>
        <div className="pt-preview-stat-label">Settings</div>
      </div>
    </div>
    <div className="pt-preview-welcome-tagline">Everything you need to support your student's learning</div>
  </div>
)

const StudentsPreview = () => (
  <div className="pt-preview-students">
    <div className="pt-preview-section-title">My Students</div>
    <div className="pt-preview-student-card pt-preview-student-card--highlight">
      <div className="pt-preview-student-avatar">{FAKE_STUDENT.initials}</div>
      <div className="pt-preview-student-info">
        <div className="pt-preview-student-name">{FAKE_STUDENT.name}</div>
        <div className="pt-preview-student-email">{FAKE_STUDENT.email}</div>
      </div>
      <div className="pt-preview-student-arrow">›</div>
    </div>
    <div className="pt-preview-add-student-hint">
      <span className="pt-preview-add-icon">+</span>
      <span>Add another student anytime</span>
    </div>
  </div>
)

const ViewWorkPreview = () => {
  const [selectedQ, setSelectedQ] = useState(FAKE_QUESTIONS[0])
  return (
    <div className="pt-preview-view-work">
      <div className="pt-preview-vw-sidebar">
        <div className="pt-preview-section-title" style={{ padding: '0 0 8px 0' }}>Emma's Work</div>
        {FAKE_QUESTIONS.map(q => (
          <div
            key={q.id}
            className={`pt-preview-vw-item ${selectedQ.id === q.id ? 'selected' : ''}`}
            onClick={() => setSelectedQ(q)}
          >
            <div className="pt-preview-vw-item-text">{q.text}</div>
            <div className="pt-preview-vw-item-meta">
              <span className={`pt-preview-vw-dot ${q.correct ? 'correct' : 'incorrect'}`} />
              <span className="pt-preview-vw-topic">{q.topic}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="pt-preview-vw-detail">
        <div className="pt-preview-vw-detail-label">Question</div>
        <div className="pt-preview-vw-detail-text">{selectedQ.text}</div>
        <div className="pt-preview-vw-detail-label" style={{ marginTop: 10 }}>
          Attempt {selectedQ.attempts} — <span className={selectedQ.correct ? 'pt-correct-text' : 'pt-incorrect-text'}>{selectedQ.correct ? '✓ Correct' : '✗ Needs work'}</span>
        </div>
        <div className="pt-preview-vw-feedback">{selectedQ.feedback}</div>
      </div>
    </div>
  )
}

const ManageWorkPreview = () => (
  <div className="pt-preview-manage-work">
    <div className="pt-preview-mw-header">
      <div className="pt-preview-section-title">Pages</div>
      <div className="pt-preview-mw-add-btn">+ Add</div>
    </div>
    {FAKE_PAGES.map(page => (
      <div key={page.id} className="pt-preview-mw-page-card">
        <div className="pt-preview-mw-page-icon">📄</div>
        <div className="pt-preview-mw-page-info">
          <div className="pt-preview-mw-page-title">Page {page.number}</div>
          <div className="pt-preview-mw-page-desc">{page.description}</div>
          <div className="pt-preview-mw-page-count">{page.questions} questions extracted</div>
        </div>
      </div>
    ))}
    <div className="pt-preview-mw-upload-hint">
      📸 Take a photo of any page to add it
    </div>
  </div>
)

const SettingsPreview = () => (
  <div className="pt-preview-settings">
    <div className="pt-preview-section-title">Settings</div>
    <div className="pt-preview-settings-card">
      <div className="pt-preview-settings-item">
        <div className="pt-preview-settings-label">AI Chat Helper</div>
        <div className="pt-preview-toggle on">
          <div className="pt-preview-toggle-knob" />
        </div>
      </div>
      <div className="pt-preview-settings-hint">Allow your student to ask the AI for hints</div>
    </div>
    <div className="pt-preview-settings-card" style={{ marginTop: 10 }}>
      <div className="pt-preview-settings-item">
        <div className="pt-preview-settings-label">Daily Activity Summary</div>
        <div className="pt-preview-toggle on">
          <div className="pt-preview-toggle-knob" />
        </div>
      </div>
      <div className="pt-preview-settings-hint">Get emailed a daily recap of your student's work</div>
    </div>
    <div className="pt-preview-settings-card" style={{ marginTop: 10 }}>
      <div className="pt-preview-settings-item">
        <div className="pt-preview-settings-label">Weekly Digest</div>
        <div className="pt-preview-toggle on">
          <div className="pt-preview-toggle-knob" />
        </div>
      </div>
      <div className="pt-preview-settings-hint">Weekly summary every Sunday</div>
    </div>
  </div>
)

const PreviewPanel = ({ stepId }) => {
  switch (stepId) {
    case 'welcome': return <WelcomePreview />
    case 'students': return <StudentsPreview />
    case 'view-work': return <ViewWorkPreview />
    case 'manage-work': return <ManageWorkPreview />
    case 'settings': return <SettingsPreview />
    default: return null
  }
}

const ParentTutorial = ({ currentUser, onComplete }) => {
  const [step, setStep] = useState(0)
  const [exiting, setExiting] = useState(false)
  const [showCelebration, setShowCelebration] = useState(false)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  const currentStep = STEPS[step]
  const isLast = step === STEPS.length - 1
  const isFirst = step === 0

  const markTutorialComplete = async () => {
    try {
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid)
        await updateDoc(userRef, { tutorialCompleted: true })
      }
    } catch (err) {
      console.error('Error marking tutorial complete:', err)
    }
  }

  const handleNext = () => {
    if (isLast) {
      handleFinish()
    } else {
      setStep(s => s + 1)
    }
  }

  const handleBack = () => {
    if (!isFirst) setStep(s => s - 1)
  }

  const handleFinish = async () => {
    await markTutorialComplete()
    setShowCelebration(true)
    setTimeout(() => {
      setShowCelebration(false)
      setExiting(true)
      setTimeout(() => {
        onComplete()
      }, 400)
    }, 1600)
  }

  const handleSkip = async () => {
    await markTutorialComplete()
    setShowCelebration(true)
    setTimeout(() => {
      setShowCelebration(false)
      setExiting(true)
      setTimeout(() => {
        onComplete()
      }, 400)
    }, 1600)
  }

  return (
    <div className={`pt-backdrop ${exiting ? 'pt-backdrop--exit' : ''}`}>
      <div className={`pt-card ${exiting ? 'pt-card--exit' : ''}`}>
        <div className="pt-card-preview">
          <div className="pt-preview-inner">
            <div className="pt-preview-browser-bar">
              <span className="pt-browser-dot" style={{ background: '#FF5F57' }} />
              <span className="pt-browser-dot" style={{ background: '#FEBC2E' }} />
              <span className="pt-browser-dot" style={{ background: '#28C840' }} />
              <span className="pt-browser-address">quillnote.app • Example Preview</span>
            </div>
            <PreviewPanel stepId={currentStep.preview} />
          </div>
        </div>

        <div className="pt-card-content">
          <div className="pt-step-indicator">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={`pt-step-dot ${i === step ? 'active' : i < step ? 'done' : ''}`}
                onClick={() => setStep(i)}
              />
            ))}
          </div>

          <div className="pt-step-emoji">{currentStep.emoji}</div>
          <div className="pt-step-title">{currentStep.title}</div>
          <div className="pt-step-subtitle">{currentStep.subtitle}</div>
          <div className="pt-step-description">{currentStep.description}</div>

          <div className="pt-card-actions">
            <div className="pt-nav-row">
              <button
                className="pt-btn-back"
                onClick={handleBack}
                style={{ visibility: isFirst ? 'hidden' : 'visible' }}
              >
                ← Back
              </button>
              <button className="pt-btn-next" onClick={handleNext}>
                {isLast ? "Let's get started! 🚀" : 'Next →'}
              </button>
            </div>
            <button className="pt-btn-skip" onClick={handleSkip}>
              Skip tutorial
            </button>
          </div>
        </div>
      </div>
      <CelebrationOverlay showConfetti={showCelebration} showXpPop={showCelebration} xpAmount={50} />
    </div>
  )
}

export default ParentTutorial
